import { type ReactElement } from 'react';
import { Info } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { OrganizationProfileSettings } from '@/pages/organization/components/ProfileSettings';
import { OrganizationKeyControl } from '@/pages/organization/components/OrganizationKeyControl';
import { OrganizationLevelKeyControl } from '@/pages/organization/components/OrganizationLevelKeyControl';
import { PluginApiKeyList } from '@/pages/organization/components/PluginApiKeyList';
import { PluginConfigDetails } from '@/pages/organization/components/PluginConfigDetails';
import { GitHubLinkButton } from '@/components/github/GitHubLinkButton';
import { ManagementApiKeysSection } from '@/components/settings/api-keys/ManagementApiKeysSection';
import type { SeverityType } from '@kirin/api-client/contracts/shared/severity';
import type { SecretConfidence } from '@kirin/api-client/helpers/secret-confidence';
import { EnforcementSeverityThresholdControl } from '../EnforcementSeverityThresholdControl';
import {
  SecretConfidenceThresholdControl,
  SecretConfidenceInfoTooltip,
} from '../SecretConfidenceThresholdControl';
import { DataCollectionToggle } from '../DataCollectionToggle';
import { InventoryVisibilityControl } from '../InventoryVisibilityControl';
import { AgentAutoUpdateSection } from '../AgentAutoUpdateSection';
import { Section } from '../Section';

type Props = {
  organizationId: string;
  canModifySettings: boolean;
  isOwner: boolean;
  isEnforcePolicyModeEnabled: boolean;
  isSecretConfidenceThresholdEnabled: boolean;
  isDataCollectionSettingsEnabled: boolean;
  isGithubIntegrationEnabled: boolean;
  isGithubConnected: boolean;
  hasAnyEnforcePolicy: boolean;
  orgEnforcementSeverity: SeverityType;
  handleOrgEnforcementSeverityChange: (severity: SeverityType) => Promise<void>;
  upsertOrgEnforcementMutationPending: boolean;
  orgSecretConfidence: SecretConfidence;
  handleOrgSecretConfidenceChange: (
    confidence: SecretConfidence
  ) => Promise<void>;
  upsertOrgSecretConfidenceMutationPending: boolean;
  orgDataCollectionEnabled: boolean;
  handleOrgDataCollectionChange: (storeInDb: boolean) => Promise<void>;
  upsertOrgDataCollectionMutationPending: boolean;
  orgInventoryHideAfterDays: number | null;
  handleOrgInventoryVisibilityChange: (
    hideAfterDays: number | null
  ) => Promise<void>;
  upsertOrgInventoryVisibilityMutationPending: boolean;
};

export function OrganizationSubTab({
  organizationId,
  canModifySettings,
  isOwner,
  isEnforcePolicyModeEnabled,
  isSecretConfidenceThresholdEnabled,
  isDataCollectionSettingsEnabled,
  isGithubIntegrationEnabled,
  isGithubConnected,
  hasAnyEnforcePolicy,
  orgEnforcementSeverity,
  handleOrgEnforcementSeverityChange,
  upsertOrgEnforcementMutationPending,
  orgSecretConfidence,
  handleOrgSecretConfidenceChange,
  upsertOrgSecretConfidenceMutationPending,
  orgDataCollectionEnabled,
  handleOrgDataCollectionChange,
  upsertOrgDataCollectionMutationPending,
  orgInventoryHideAfterDays,
  handleOrgInventoryVisibilityChange,
  upsertOrgInventoryVisibilityMutationPending,
}: Props): ReactElement {
  return (
    <div className="space-y-7">
      {/* S1 — General */}
      {canModifySettings && (
        <Section
          title="General"
          description="Manage your organization's basic information"
        >
          <OrganizationProfileSettings organizationId={organizationId} />
        </Section>
      )}

      {/* S2 — API Keys */}
      <Section
        title="API Keys"
        description="Manage your API keys to authenticate Kirin scans and protect your development environment"
      >
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Your Personal Organization API Key</CardTitle>
              <CardDescription>
                Generate your personal API key for this organization. Each
                member has their own user-specific key scoped to this
                organization.
                <br />
                Use this key to protect your personal development environment
                and authenticate Kirin scans for your individual workspace.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <OrganizationKeyControl organizationId={organizationId} />
            </CardContent>
          </Card>

          {isOwner && (
            <Card>
              <CardHeader>
                <CardTitle>Global Organization MDM API Key</CardTitle>
                <CardDescription>
                  Generate a global organization API key for MDM installation.
                  This shared key allows you to deploy Kirin across your entire
                  organization without requiring individual user API keys. All
                  owners can view the key prefix.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <OrganizationLevelKeyControl organizationId={organizationId} />
              </CardContent>
            </Card>
          )}

          {/* Self-gating on the `management_api_keys` flag + role — renders
              null when either says no, so no props are threaded for it. */}
          <ManagementApiKeysSection organizationId={organizationId} />

          {isOwner && (
            <Card>
              <CardHeader>
                <CardTitle>Global Organization Plugin API Keys</CardTitle>
                <CardDescription>
                  Plugin API keys are baked into the Claude Desktop hook plugin
                  zip when you click <strong>Download Plugin</strong> on the Get
                  Started → Claude Desktop tab. Multiple keys can be active at
                  once; downloading a new plugin schedules the existing active
                  key to expire after 7 days.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm font-medium mb-1">
                    Plugin configuration
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">
                    Values baked into the plugin zip when you click{' '}
                    <strong>Download Plugin</strong>.
                  </div>
                  <PluginConfigDetails organizationId={organizationId} />
                </div>
                <PluginApiKeyList organizationId={organizationId} />
              </CardContent>
            </Card>
          )}
        </div>
      </Section>

      {/* S3 — Policy Settings */}
      {canModifySettings &&
        (isEnforcePolicyModeEnabled || isSecretConfidenceThresholdEnabled) && (
          <Section
            title="Policy Settings"
            description="Configure global settings that govern how policies detect and enforce"
          >
            {isEnforcePolicyModeEnabled && (
              <Card>
                <CardHeader>
                  <CardTitle>Enforcement Severity Threshold</CardTitle>
                  <CardDescription>
                    When policies are set to Enforce, this threshold determines
                    the minimum severity at which protective actions are
                    applied.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <EnforcementSeverityThresholdControl
                    value={orgEnforcementSeverity}
                    onChange={handleOrgEnforcementSeverityChange}
                    disabled={!hasAnyEnforcePolicy}
                    saving={upsertOrgEnforcementMutationPending}
                    canEdit={canModifySettings}
                  />
                </CardContent>
              </Card>
            )}

            {isSecretConfidenceThresholdEnabled && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-1.5">
                    Secret Alert Confidence Threshold
                    <SecretConfidenceInfoTooltip />
                  </CardTitle>
                  <CardDescription>
                    Secret leakage alerts below the chosen confidence are hidden
                    from the dashboard, won't notify, and won't be blocked in
                    Enforce mode. Secrets matched by the regex engine have no
                    confidence score and are always shown.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <SecretConfidenceThresholdControl
                    value={orgSecretConfidence}
                    onChange={handleOrgSecretConfidenceChange}
                    saving={upsertOrgSecretConfidenceMutationPending}
                    canEdit={canModifySettings}
                  />
                </CardContent>
              </Card>
            )}
          </Section>
        )}

      {/* S3.5 — Inventory visibility */}
      {canModifySettings && (
        <Section
          title="Inventory"
          description="Control how your organization's inventory is displayed"
        >
          <Card>
            <CardHeader>
              <CardTitle>
                Automatically hide stale entries from inventory
              </CardTitle>
              <CardDescription>
                Hide entries from the Inventory view when they have not been
                observed by Kirin for the selected period. Entries may reappear
                if Kirin detects them again.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <div className="text-sm font-medium">Hide entries after</div>
                <InventoryVisibilityControl
                  value={orgInventoryHideAfterDays}
                  onChange={handleOrgInventoryVisibilityChange}
                  saving={upsertOrgInventoryVisibilityMutationPending}
                  canEdit={canModifySettings}
                />
              </div>
              <div className="flex items-start gap-2 text-xs italic text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <p>
                  This setting only affects inventory visibility and does not
                  change your organization's data-retention policy.
                </p>
              </div>
            </CardContent>
          </Card>
        </Section>
      )}

      {/* S3.6 — Agent updates. Self-gating on its flag; read-only below Manager. */}
      <AgentAutoUpdateSection
        organizationId={organizationId}
        canModifySettings={canModifySettings}
      />

      {/* S4 — Privacy */}
      {canModifySettings && isDataCollectionSettingsEnabled && (
        <Section
          title="Privacy"
          description="Control how your organization's data is used to improve Kirin"
        >
          <Card className="rounded-xl border-border/60 bg-card/80">
            <CardContent className="p-6">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <h3 className="text-base font-semibold">
                      Help improve Kirin for everyone
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Allow Kirin to use anonymized, operational data from your
                      agent sessions — such as policy matches and detection
                      events — to improve accuracy and performance for all
                      users. Your files and personal data are never collected.
                    </p>
                  </div>
                  <div className="flex items-start gap-2 text-xs italic text-muted-foreground">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <p>
                      Turning this off excludes your organization's data from
                      model training and product improvement. This setting
                      applies to all users in your organization and does not
                      affect product functionality.
                    </p>
                  </div>
                </div>
                <div className="sm:pt-0.5">
                  <DataCollectionToggle
                    value={orgDataCollectionEnabled}
                    onChange={handleOrgDataCollectionChange}
                    saving={upsertOrgDataCollectionMutationPending}
                    canEdit={canModifySettings}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </Section>
      )}

      {/* S5 — Your Account */}
      {isGithubIntegrationEnabled && isGithubConnected && (
        <Section
          title="Your Account"
          description="Link your personal GitHub account for identity tracking on commits and pull requests."
        >
          <GitHubLinkButton organizationId={organizationId} variant="full" />
        </Section>
      )}
    </div>
  );
}
